/**
 * Обсуждение элемента.
 *
 * Комментарии нужны для уточнений и проверки, но не заменяют проектные
 * записи: если в обсуждении принято решение, его фиксируют отдельным
 * элементом типа «Решение» (документ «Итоговое описание», §18).
 *
 * Удалённый комментарий остаётся в ленте отметкой — иначе обсуждение
 * рассыпается на реплики без контекста.
 */

import { Loader2Icon, MessageSquareIcon, PencilIcon, SendIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { CommentDto } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import {
  useComments,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
} from '@/api/collaboration';
import { EmptyState, ErrorState } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDateTime, formatRelative } from '@/lib/format';

export function CommentsSection({
  projectId,
  itemId,
  canComment,
}: {
  projectId: string;
  itemId: string;
  canComment: boolean;
}) {
  const { data, isPending, isError, error, refetch } = useComments(projectId, itemId);
  const create = useCreateComment(projectId, itemId);
  const [draft, setDraft] = useState('');

  const submit = async () => {
    const bodyMd = draft.trim();
    if (!bodyMd) return;
    try {
      await create.mutateAsync({ bodyMd });
      setDraft('');
    } catch (mutationError) {
      toast.error(
        mutationError instanceof ApiError ? mutationError.message : 'Не удалось отправить',
      );
    }
  };

  return (
    <div className="space-y-4">
      {isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : data.length === 0 ? (
        <EmptyState
          icon={<MessageSquareIcon />}
          title="Обсуждения пока нет"
          description="Комментарии удобны для уточнений и проверки. Принятое решение лучше зафиксировать отдельным элементом — иначе оно потеряется в переписке."
        />
      ) : (
        <ul className="space-y-3">
          {data.map((comment) => (
            <CommentRow key={comment.id} projectId={projectId} itemId={itemId} comment={comment} />
          ))}
        </ul>
      )}

      {canComment ? (
        <div className="space-y-2">
          <Textarea
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Написать комментарий…"
            aria-label="Текст комментария"
            onKeyDown={(event) => {
              // Ctrl+Enter отправляет: обычный Enter должен переносить строку.
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                void submit();
              }
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-2xs text-muted-foreground">Ctrl + Enter — отправить</span>
            <Button
              size="sm"
              disabled={!draft.trim() || create.isPending}
              onClick={() => void submit()}
            >
              {create.isPending ? <Loader2Icon className="animate-spin" /> : <SendIcon />}
              Отправить
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CommentRow({
  projectId,
  itemId,
  comment,
}: {
  projectId: string;
  itemId: string;
  comment: CommentDto;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.bodyMd);

  const update = useUpdateComment(projectId, itemId);
  const remove = useDeleteComment(projectId, itemId);

  const save = async () => {
    const bodyMd = draft.trim();
    if (!bodyMd) return;
    try {
      await update.mutateAsync({ commentId: comment.id, bodyMd });
      setEditing(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось сохранить');
    }
  };

  const drop = async () => {
    try {
      await remove.mutateAsync(comment.id);
      toast.success('Комментарий удалён');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось удалить');
    }
  };

  const edited = comment.updatedAt !== comment.createdAt && !comment.deletedAt;

  return (
    <li className="flex gap-2.5">
      <UserAvatar user={comment.author} size="md" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium">{comment.author.name}</span>
          <span
            className="text-2xs text-muted-foreground"
            title={formatDateTime(comment.createdAt)}
          >
            {formatRelative(comment.createdAt)}
          </span>
          {edited ? <span className="text-2xs text-muted-foreground">изменён</span> : null}
        </div>

        {comment.deletedAt ? (
          <p className="mt-1 text-sm text-muted-foreground/70 italic">Комментарий удалён</p>
        ) : editing ? (
          <div className="mt-1.5 space-y-2">
            <Textarea rows={3} value={draft} onChange={(event) => setDraft(event.target.value)} />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(comment.bodyMd);
                  setEditing(false);
                }}
              >
                Отмена
              </Button>
              <Button size="sm" disabled={update.isPending} onClick={() => void save()}>
                Сохранить
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">{comment.bodyMd}</p>
        )}

        {comment.canModify && !editing ? (
          <div className="mt-1 flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-2xs text-muted-foreground"
              onClick={() => setEditing(true)}
            >
              <PencilIcon className="size-3" />
              Изменить
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-2xs text-muted-foreground hover:text-destructive"
              disabled={remove.isPending}
              onClick={() => void drop()}
            >
              <TrashIcon className="size-3" />
              Удалить
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
